import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Truck, PackageCheck, Package, ClipboardCheck, ShoppingBag, ExternalLink, Search, XCircle, MessageSquare } from "lucide-react";
import { useCustomer } from "@/context/CustomerContext";
import { formatINR, TRACK_ORDER_URL } from "@/config";
import { api, formatApiError } from "@/lib/api";
import { ItemDetailsSummary } from "./ItemDetailsSummary";

export const ORDER_STAGES = [
  { key: "placed", label: "Order placed", icon: ShoppingBag },
  { key: "confirmed", label: "Confirmed", icon: ClipboardCheck },
  { key: "packed", label: "Packed", icon: Package },
  { key: "dispatched", label: "Dispatched", icon: Truck },
  { key: "delivered", label: "Delivered", icon: PackageCheck },
];

const stageIndex = (stage) => ORDER_STAGES.findIndex((s) => s.key === stage);

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

const SIDE_STAGES = {
  discussion: { label: "In discussion with our team", cls: "border-violet-400/30 bg-violet-400/5 text-violet-200" },
  pending: { label: "Pending — awaiting confirmation", cls: "border-orange-400/30 bg-orange-400/5 text-orange-200" },
};

function StageTracker({ order }) {
  const cancelled = order.stage === "cancelled";
  const side = SIDE_STAGES[order.stage];
  const current = cancelled ? -1 : Math.max(0, stageIndex(order.stage || "placed"));
  const stampFor = (key) => order.timeline?.find((t) => t.stage === key)?.at;

  if (cancelled) {
    return (
      <div data-testid={`order-cancelled-${order.id}`} className="flex items-center gap-2 rounded-xl border border-red-400/30 bg-red-400/5 px-3 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-red-300">
        <XCircle size={14} /> Order cancelled
      </div>
    );
  }

  return (
    <>
      {side && (
        <div data-testid={`order-side-stage-${order.id}`} className={`mb-3 flex items-center gap-2 rounded-xl border px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] ${side.cls}`}>
          <MessageSquare size={13} /> {side.label}
        </div>
      )}
      <div className="flex items-start" data-testid={`order-stage-tracker-${order.id}`}>
        {ORDER_STAGES.map((s, i) => {
          const done = i <= current;
          const Icon = s.icon;
          return (
            <div key={s.key} className="flex min-w-0 flex-1 flex-col items-center">
              <div className="flex w-full items-center">
                <span className={`h-[2px] flex-1 ${i === 0 ? "opacity-0" : done ? "bg-cyan-400" : "bg-white/10"}`} />
                <span
                  data-testid={`order-${order.id}-stage-${s.key}`}
                  data-done={done ? "1" : "0"}
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors ${
                    i === current
                      ? "border-cyan-400 bg-cyan-400/15 text-cyan-300 shadow-[0_0_0_4px_rgba(34,211,238,0.12)]"
                      : done
                        ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-300"
                        : "border-white/10 bg-[#0A0B0E] text-slate-600"
                  }`}
                >
                  <Icon size={13} />
                </span>
                <span className={`h-[2px] flex-1 ${i === ORDER_STAGES.length - 1 ? "opacity-0" : i < current ? "bg-cyan-400" : "bg-white/10"}`} />
              </div>
              <span className={`mt-2 text-center font-mono text-[9px] uppercase leading-tight tracking-[0.1em] ${done ? "text-slate-300" : "text-slate-600"}`}>
                {s.label}
              </span>
              {stampFor(s.key) && (
                <span className="mt-1 text-center font-mono text-[9px] leading-tight text-slate-400">{fmtDate(stampFor(s.key))}</span>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function OrderCard({ order }) {
  const dispatched = stageIndex(order.stage) >= stageIndex("dispatched");
  return (
    <div data-testid={`my-order-${order.id}`} className="rounded-2xl border border-white/10 bg-[#0A0B0E]/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            #{order.id.slice(-6).toUpperCase()}
          </div>
          <div className="mt-0.5 font-mono text-[10px] text-slate-600">{fmtDate(order.created_at)}</div>
        </div>
        <span data-testid={`my-order-total-${order.id}`} className="font-mono text-sm font-bold text-yellow-300">{formatINR(order.total)}</span>
      </div>

      <div className="mt-4">
        <StageTracker order={order} />
      </div>

      {dispatched && order.stage !== "cancelled" && (
        <div className="mt-4 rounded-xl border border-cyan-400/25 bg-cyan-400/[0.04] p-3" data-testid={`my-order-courier-${order.id}`}>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-300">
            {order.courier_name || "Courier"}
          </div>
          {order.tracking_number && (
            <div className="mt-1 font-mono text-xs text-slate-300" data-testid={`my-order-awb-${order.id}`}>
              AWB {order.tracking_number}
            </div>
          )}
          {order.tracking_url && (
            <a
              href={order.tracking_url}
              target="_blank"
              rel="noreferrer"
              data-testid={`my-order-track-btn-${order.id}`}
              className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-xs font-bold uppercase tracking-[0.15em] text-[#0A0B0E] transition-transform hover:scale-[1.02] active:scale-95"
            >
              <Truck size={14} /> Track Order <ExternalLink size={12} />
            </a>
          )}
        </div>
      )}

      <div className="mt-3 space-y-1 font-mono text-[11px] text-slate-400">
        {order.items.map((i, idx) => (
          <div key={`${i.product_id}-${idx}`} data-testid={`my-order-item-${order.id}-${idx}`}>
            • {i.name}{i.sku ? <span className="text-cyan-300"> [{i.sku}]</span> : null} x{i.qty}
            <ItemDetailsSummary item={i} prefix={`my-order-item-${order.id}-${idx}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MyOrders() {
  const { customer } = useCustomer();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("phone");
  const [phone, setPhone] = useState(customer?.phone || "");
  const [awb, setAwb] = useState("");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState("");

  const load = useCallback(async (number) => {
    const clean = (number || "").replace(/\D/g, "");
    if (clean.length < 10) {
      setHint("Enter the full 10-digit number you ordered with");
      return;
    }
    setHint("");
    setLoading(true);
    try {
      const { data } = await api.get(`/my-orders/${clean}`);
      setOrders(data);
    } catch (e) {
      setOrders([]);
      if (e?.response) console.warn(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadByAwb = useCallback(async (number) => {
    const clean = (number || "").trim();
    if (clean.length < 4) {
      setHint("Enter the AWB / tracking number printed on your parcel");
      return;
    }
    setHint("");
    setLoading(true);
    try {
      const { data } = await api.get(`/track/awb/${encodeURIComponent(clean)}`);
      setOrders([data]);
    } catch (e) {
      setOrders([]);
      setHint(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const search = () => (mode === "awb" ? loadByAwb(awb) : load(phone));

  useEffect(() => {
    const show = () => {
      setHint("");
      if (customer?.phone) setPhone(customer.phone);
      setOpen(true);
    };
    window.addEventListener("vs:my-orders", show);
    return () => window.removeEventListener("vs:my-orders", show);
  }, [customer?.phone]);

  useEffect(() => {
    if (customer?.phone) setPhone(customer.phone);
  }, [customer?.phone]);

  useEffect(() => {
    if (open && mode === "phone" && customer?.phone) load(customer.phone);
  }, [open, mode, customer?.phone, load]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[60] bg-[#0A0B0E]/70 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 34 }}
            data-testid="my-orders-drawer"
            className="fixed right-0 top-0 z-[65] flex h-full w-full max-w-md flex-col border-l border-white/10 bg-[#12141A]"
          >
            <div className="flex items-center justify-between border-b border-white/10 p-5">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400">Tracking</div>
                <h3 className="font-display text-xl font-bold">My Orders</h3>
              </div>
              <button
                data-testid="close-my-orders-btn"
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-slate-400 transition-colors hover:border-fuchsia-400 hover:text-fuchsia-300"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              <div className="mb-3 flex gap-1.5 rounded-full border border-white/10 bg-[#0A0B0E]/60 p-1" data-testid="my-orders-mode-switch">
                {[
                  { key: "phone", label: "By mobile" },
                  { key: "awb", label: "By AWB number" },
                ].map((m) => (
                  <button
                    key={m.key}
                    data-testid={`my-orders-mode-${m.key}`}
                    onClick={() => {
                      setMode(m.key);
                      setHint("");
                      setOrders(null);
                      if (m.key === "awb") window.open(TRACK_ORDER_URL, "_blank", "noopener,noreferrer");
                    }}
                    className={`flex-1 rounded-full py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                      mode === m.key ? "bg-gradient-to-r from-cyan-400 to-fuchsia-500 font-bold text-[#0A0B0E]" : "text-slate-400 hover:text-cyan-300"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                {mode === "awb" ? (
                  <input
                    data-testid="my-orders-awb-input"
                    value={awb}
                    onChange={(e) => setAwb(e.target.value.toUpperCase().slice(0, 40))}
                    onKeyDown={(e) => { if (e.key === "Enter") loadByAwb(awb); }}
                    placeholder="Courier AWB / tracking number"
                    className="w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-3 font-mono text-sm uppercase placeholder:font-sans placeholder:normal-case placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
                  />
                ) : (
                  <input
                    data-testid="my-orders-phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 12))}
                    onKeyDown={(e) => { if (e.key === "Enter") load(phone); }}
                    inputMode="numeric"
                    placeholder="Your order mobile number"
                    className="w-full rounded-xl border border-white/10 bg-[#0A0B0E]/60 px-4 py-3 font-mono text-sm placeholder:font-sans placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
                  />
                )}
                <button
                  data-testid="my-orders-search-btn"
                  onClick={search}
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-cyan-400/40 text-cyan-300 transition-colors hover:bg-cyan-400/10"
                >
                  <Search size={16} />
                </button>
              </div>
              {hint && (
                <p data-testid="my-orders-hint" className="mt-2 font-mono text-[11px] text-yellow-300">{hint}</p>
              )}
              {mode === "awb" && (
                <a
                  href={TRACK_ORDER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="my-orders-icarry-link"
                  className="mt-3 flex items-center justify-center gap-2 rounded-xl border border-white/15 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-300 transition-colors hover:border-cyan-400/50 hover:text-cyan-300"
                >
                  <ExternalLink size={12} /> Track on the courier site
                </a>
              )}

              <div className="mt-5 space-y-4">
                {loading && (
                  <div data-testid="my-orders-loading" className="py-10 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    Loading your orders…
                  </div>
                )}
                {!loading && orders === null && (
                  <div data-testid="my-orders-prompt" className="py-10 text-center text-sm leading-relaxed text-slate-500">
                    {mode === "awb"
                      ? <>Enter the courier AWB / tracking number<br />to jump straight to that parcel.</>
                      : <>Enter the mobile number you ordered with<br />to see every order and its live stage.</>}
                  </div>
                )}
                {!loading && orders?.length === 0 && (
                  <div data-testid="my-orders-empty" className="py-10 text-center text-sm text-slate-500">
                    {mode === "awb" ? "No order found for this AWB number yet." : "No orders found for this number yet."}
                  </div>
                )}
                {!loading && orders?.map((o) => <OrderCard key={o.id} order={o} />)}
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
